// pms-adapters / services — the driving-port implementations (application layer).
//
// These hold DRIVEN ports (constructor injection = IoC) and expose the use-case
// API drivers depend on. The composition root wires concrete adapters in; a test
// wires in-memory fakes. No Supabase symbol appears here — only pms-core ports.
//
// ── The L2 capability gate (ADR-0013 §4, finding F3) ────────────────────────
//
// Every method below opens with `gate(...)`. Two things it is NOT:
//
//  1. NOT the security boundary. The browser holds a real Postgres session, so
//     RLS and the SECURITY DEFINER RPCs decide (ADR-0013 §7). Every capability
//     named here has a matching policy at L4 — that is the ADR's rule, and it
//     is what makes this layer removable without changing who can do what.
//  2. NOT a second source of truth. The set comes from `my_capabilities()`,
//     the same rows `has_capability()` reads, so the gate and the policy cannot
//     disagree about a principal.
//
// What it buys is the thing L4 cannot give: a denial that is a domain outcome.
// Without it a receptionist without `pms.reservation.write` gets a raw
// PostgREST error surfaced through `toPmsError`; with it they get a French
// sentence, before any round trip. Hence `err('forbidden', …)` — the kind
// pms-core already defines as "RLS / role gate refused".

import {
  CAPABILITIES,
  assertCapability,
  type ActorSource,
  type Capability,
} from '@lemontaigu/platform';
import {
  err,
  bankTransferProblem,
  cancellationPolicyProblem,
  transferDueProblem,
  termsRulesProblem,
  tillDraftProblem,
  ok,
  pmsIsChannexId,
  pmsNormalizeChannexId,
  pmsValidateGuestSheet,
  validateRateParameterSet,
  type AssignRoomStayOptions,
} from '@lemontaigu/pms-core';
import type {
  CancellationFeeLine,
  CancellationReason,
  StayTermsBase,
  TrainingResetReport,
  AvailabilityCell,
  AvailabilityQuery,
  AvailabilityService,
  RateParameterService,
  RateParameterSet,
  StoredRateParameterSet,
  BedConfig,
  BedType,
  BillingMode,
  Cents,
  ChannexRatePlanLink,
  Charge,
  ChargeDay,
  ChargeId,
  Folio,
  FolioId,
  OccupancySpan,
  StayPerson,
  StayPersonMeal,
  StayPersonNeeds,
  OccupancySpanInput,
  OtaService,
  Product,
  ProductDraft,
  ProductId,
  RoomBed,
  RoomConfigPatch,
  NewRoom,
  CreateReservationInput,
  CustomerSubtype,
  Err,
  GuestId,
  GuestRef,
  GuestRepository,
  GuestSearch,
  GuestSearchResult,
  GuestService,
  GuestSheet,
  GuestSheetPatch,
  IsoDate,
  Rate,
  RateGridService,
  RatePlan,
  RatePlanId,
  RateRepository,
  ReferenceService,
  TillCarte,
  TillCatalogueRepository,
  TillCatalogueService,
  BankTransferDetails,
  BankTransferRepository,
  BankTransferService,
  CancellationPolicy,
  CancellationPolicyRepository,
  CancellationPolicyService,
  StoredCancellationPolicy,
  TillProductDraft,
  Reservation,
  ReservationEvent,
  ReservationId,
  ReservationCommand,
  ReservationCommandRoom,
  ReservationPatch,
  ReservationRepository,
  ReservationService,
  RoomStayInput,
  ReservationView,
  RackWindow,
  BookingSearchResult,
  Result,
  Room,
  RoomId,
  RoomStayId,
  RoomStayView,
  RoomRepository,
  RoomType,
  RoomTypeId,
  TransitionAction,
} from '@lemontaigu/pms-core';

/**
 * The gate, as a `Result` so a denial composes with everything else a service
 * method can return. `null` means "allowed" — the caller proceeds.
 *
 * Returning `Err` rather than throwing is ADR-0013 §4: being refused is an
 * expected outcome of asking, not an exceptional one. `retryable: false`
 * because retrying without new capabilities cannot succeed.
 *
 * EXPORTED so `channelOps.ts` uses this gate rather than a second one shaped
 * like it. It is not part of the package's public surface (`index.ts` does not
 * re-export it) — a driver that reached for it would be doing the L2 check in
 * the layer that is supposed to receive its result.
 */
export function gate(actor: ActorSource, cap: Capability): Err | null {
  const check = assertCapability(actor.current(), cap);
  return check.ok ? null : err('forbidden', check.message);
}

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * PB4 — what the desk may add to a command, as one French sentence or null:
 * the client mirror of `pms_reservation_apply`'s checks, so the screen answers
 * before the round trip. The database refuses the same commands anyway.
 */
export function deskCommandProblem(command: ReservationCommand): string | null {
  const { booking, envelope, rooms } = command;
  if (booking.guarantee !== undefined) {
    if (envelope.mode !== 'create') return 'La garantie se choisit à la création — ensuite « VAD faite » la fait avancer.';
    if (booking.guarantee !== 'none' && booking.guarantee !== 'vad_pending' && booking.guarantee !== 'transfer_pending') {
      return 'Garantie : lien VAD, virement ou aucune.';
    }
  }
  if (booking.vadAmountCents !== undefined) {
    if (!Number.isInteger(booking.vadAmountCents) || booking.vadAmountCents < 0) return 'Arrhes : un montant en centimes, 0 ou plus.';
    if (envelope.mode === 'create' && booking.guarantee !== 'vad_pending' && booking.guarantee !== 'transfer_pending') {
      return 'Des arrhes supposent une garantie par lien VAD ou par virement.';
    }
  }
  if (envelope.mode === 'create') {
    if (booking.guarantee === 'transfer_pending') {
      if (booking.transferDueOn === undefined) return 'Un virement demande son échéance.';
      if (!booking.vadAmountCents || booking.vadAmountCents <= 0) return 'Un virement suppose des arrhes à recevoir (montant supérieur à 0).';
      // Checked against the dates the command itself carries: the database re-checks against the clock.
      const arrival = rooms.map((r) => r.checkin).sort()[0];
      if (arrival !== undefined) {
        const problem = transferDueProblem(booking.transferDueOn, '0000-01-01', arrival);
        if (problem !== null) return problem;
      }
    } else if (booking.transferDueOn !== undefined) {
      return 'Une échéance de virement suppose une garantie par virement.';
    }
  }
  for (const t of [booking.arrivalFrom, booking.arrivalTo]) {
    if (t != null && !HHMM.test(t)) return 'Heure d’arrivée : HH:MM.';
  }
  if (booking.arrivalFrom != null && booking.arrivalTo != null && booking.arrivalFrom > booking.arrivalTo) {
    return 'Fenêtre d’arrivée : le début précède la fin.';
  }
  for (const room of rooms) {
    if (room.cancellationTerms === undefined) continue;
    if (envelope.source !== 'manual') return 'Seul le bureau assouplit les conditions d’une réservation.';
    const terms = termsRulesProblem(room.cancellationTerms);
    if (terms !== null) return `Conditions assouplies : ${terms}`;
  }
  return null;
}

/**
 * The desk form's input → the one write command (A1).
 *
 * Both refuse an input without `rooms`: the command is always the complete set
 * of logements (D2), and the flat single-room fields cannot say which stored
 * room they mean. Rack and Réservations always send `rooms`
 * (`buildReservationWrite`), so the refusal guards a caller that does not exist
 * yet rather than changing one that does.
 *
 * A room's `products` becomes `[]` when absent. That is not a reload: the
 * form's `RoomStayInput` has always meant « absent = none », and the command
 * makes that explicit instead of leaving it to the RPC to infer.
 */
export function commandForBook(
  guest: GuestRef,
  input: Omit<CreateReservationInput, 'guestId'>,
): Result<ReservationCommand> {
  if (input.source !== 'manual') {
    return err('validation', `Source « ${input.source} » : seule la saisie manuelle passe par le formulaire.`);
  }
  if (!input.rooms || input.rooms.length === 0) {
    return err('validation', 'Une réservation doit compter au moins un logement.');
  }
  return ok({
    envelope: { source: 'manual', mode: 'create', reservationId: null, externalRef: null, revision: null },
    guest,
    booking: {
      status: input.status,
      channel: input.channel,
      channelRef: input.channelRef,
      notes: input.notes,
    },
    rooms: input.rooms.map(commandRoom),
  });
}

export function commandForUpdate(
  id: ReservationId,
  guest: GuestRef,
  patch: Omit<ReservationPatch, 'guestId'>,
): Result<ReservationCommand> {
  if (!patch.rooms || patch.rooms.length === 0) {
    return err('validation', 'La modification doit donner la liste complète des logements.');
  }
  // Only the keys the patch carries: an absent one keeps the stored value.
  const booking: ReservationCommand['booking'] = {};
  if (patch.channel !== undefined) booking.channel = patch.channel;
  if (patch.channelRef !== undefined) booking.channelRef = patch.channelRef;
  if (patch.notes !== undefined) booking.notes = patch.notes;
  return ok({
    envelope: { source: 'manual', mode: 'patch', reservationId: id, externalRef: null, revision: null },
    guest,
    booking,
    rooms: patch.rooms.map(commandRoom),
  });
}

function commandRoom(r: RoomStayInput): ReservationCommandRoom {
  return { ...r, products: r.products ?? [] };
}

export class ReservationServiceImpl implements ReservationService {
  constructor(
    private readonly reservations: ReservationRepository,
    private readonly actor: ActorSource,
  ) {}

  /**
   * ONE repository call, not two (review R4 / F16).
   *
   * This used to be `guests.upsert()` then `reservations.create()`. Both are
   * atomic RPCs, but a composition of two atomic operations is not itself
   * atomic: the upsert committed before the create was sent, so every `raise`
   * in the create — including the ones the desk form does not pre-validate —
   * left an orphan guest row behind, and each retry added another. An
   * application service is not a place where a transaction can be opened, so
   * the composition had to move into the database (migration 0065).
   */
  apply(command: ReservationCommand): Promise<Result<ReservationId>> {
    // A write touches BOTH aggregates, exactly as pms_reservation_apply does —
    // the gate names the same pair the RPC requires, so L2 and L4 refuse the
    // same caller for the same reason.
    const denied =
      gate(this.actor, CAPABILITIES.PMS_GUEST_WRITE) ??
      gate(this.actor, CAPABILITIES.PMS_RESERVATION_WRITE);
    if (denied) return Promise.resolve(denied);
    const problem = deskCommandProblem(command);
    if (problem !== null) return Promise.resolve(err('validation', problem));
    return this.reservations.apply(command);
  }

  book(
    guest: GuestRef,
    input: Omit<CreateReservationInput, 'guestId'>,
  ): Promise<Result<ReservationId>> {
    const command = commandForBook(guest, input);
    return command.ok ? this.apply(command.value) : Promise.resolve(command);
  }

  async update(
    id: ReservationId,
    guest: GuestRef,
    patch: Omit<ReservationPatch, 'guestId'>,
  ): Promise<Result<void>> {
    const command = commandForUpdate(id, guest, patch);
    if (!command.ok) return command;
    const r = await this.apply(command.value);
    return r.ok ? { ok: true, value: undefined } : r;
  }

  assignRoom(id: ReservationId, roomId: RoomId | null): Promise<Result<void>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_WRITE);
    if (denied) return Promise.resolve(denied);
    return this.reservations.assignRoom(id, roomId);
  }

  assignRoomStay(id: RoomStayId, roomId: RoomId | null, opts?: AssignRoomStayOptions): Promise<Result<void>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_WRITE);
    if (denied) return Promise.resolve(denied);
    return this.reservations.assignRoomStay(id, roomId, opts);
  }

  transition(
    id: ReservationId,
    action: TransitionAction,
    reason?: string | null,
    reasonCode?: string | null,
    fee?: readonly CancellationFeeLine[] | null,
  ): Promise<Result<Reservation['status']>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_WRITE);
    if (denied) return Promise.resolve(denied);
    return this.reservations.transition(id, action, reason, reasonCode, fee);
  }

  cancellationReasons(): Promise<Result<CancellationReason[]>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_READ);
    if (denied) return Promise.resolve(denied);
    return this.reservations.cancellationReasons();
  }

  termsBase(id: ReservationId): Promise<Result<StayTermsBase[]>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_READ);
    if (denied) return Promise.resolve(denied);
    return this.reservations.termsBase(id);
  }

  currentDay(propertyCode: string): Promise<Result<IsoDate | null>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_READ);
    if (denied) return Promise.resolve(denied);
    return this.reservations.currentDay(propertyCode);
  }

  closeDay(propertyCode: string, expectedDay: IsoDate): Promise<Result<IsoDate>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_DAY_CLOSE);
    if (denied) return Promise.resolve(denied);
    return this.reservations.closeDay(propertyCode, expectedDay);
  }

  resetTraining(propertyCode: string): Promise<Result<TrainingResetReport>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_TRAINING_RESET);
    if (denied) return Promise.resolve(denied);
    return this.reservations.resetTraining(propertyCode);
  }

  transitionRoomStay(
    id: RoomStayId,
    action: TransitionAction,
    reason?: string | null,
  ): Promise<Result<Reservation['status']>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_WRITE);
    if (denied) return Promise.resolve(denied);
    return this.reservations.transitionRoomStay(id, action, reason);
  }

  roomStays(id: ReservationId): Promise<Result<RoomStayView[]>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_READ);
    if (denied) return Promise.resolve(denied);
    return this.reservations.roomStays(id);
  }

  listAllRoomStays(): Promise<Result<RoomStayView[]>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_READ);
    if (denied) return Promise.resolve(denied);
    return this.reservations.listAllRoomStays();
  }

  rackWindow(from: IsoDate, to: IsoDate): Promise<Result<RackWindow>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_READ);
    if (denied) return Promise.resolve(denied);
    return this.reservations.rackWindow(from, to);
  }

  searchBookings(query: string, limit?: number): Promise<Result<BookingSearchResult>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_READ);
    if (denied) return Promise.resolve(denied);
    return this.reservations.searchBookings(query, limit);
  }

  list(): Promise<Result<ReservationView[]>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_READ);
    if (denied) return Promise.resolve(denied);
    return this.reservations.listAll();
  }

  listArriving(from: IsoDate, to: IsoDate): Promise<Result<ReservationView[]>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_READ);
    if (denied) return Promise.resolve(denied);
    return this.reservations.listArriving(from, to);
  }

  get(id: ReservationId): Promise<Result<ReservationView | null>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_READ);
    if (denied) return Promise.resolve(denied);
    return this.reservations.findById(id);
  }

  history(id: ReservationId): Promise<Result<ReservationEvent[]>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_READ);
    if (denied) return Promise.resolve(denied);
    return this.reservations.events(id);
  }

  chargeDays(id: ReservationId): Promise<Result<ChargeDay[]>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_READ);
    if (denied) return Promise.resolve(denied);
    return this.reservations.chargeDays(id);
  }

  // ── The folio (0096-0099) ─────────────────────────────────────────────────
  // Reads gate on READ, writes on WRITE. Editing occupancy is a WRITE and not a
  // configuration act: it changes what a guest owes and what is declared to the
  // commune. So is re-laying folios out — it decides who is asked to pay.

  charges(id: ReservationId): Promise<Result<Charge[]>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_READ);
    if (denied) return Promise.resolve(denied);
    return this.reservations.charges(id);
  }

  folios(id: ReservationId): Promise<Result<Folio[]>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_READ);
    if (denied) return Promise.resolve(denied);
    return this.reservations.folios(id);
  }

  occupancy(id: RoomStayId): Promise<Result<OccupancySpan[]>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_READ);
    if (denied) return Promise.resolve(denied);
    return this.reservations.occupancy(id);
  }

  persons(id: ReservationId): Promise<Result<StayPerson[]>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_READ);
    if (denied) return Promise.resolve(denied);
    return this.reservations.persons(id);
  }

  personMeals(id: ReservationId): Promise<Result<StayPersonMeal[]>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_READ);
    if (denied) return Promise.resolve(denied);
    return this.reservations.personMeals(id);
  }

  personNeeds(id: ReservationId): Promise<Result<StayPersonNeeds[]>> {
    // Health data: its own capability, not pms.reservation.read (PMS-GROUP-EDIT-BRIEF §6).
    const denied = gate(this.actor, CAPABILITIES.PMS_NEEDS_READ);
    if (denied) return Promise.resolve(denied);
    return this.reservations.personNeeds(id);
  }

  otaServices(id: ReservationId): Promise<Result<OtaService[]>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_READ);
    if (denied) return Promise.resolve(denied);
    return this.reservations.otaServices(id);
  }

  writeOccupancy(id: RoomStayId, spans: readonly OccupancySpanInput[]): Promise<Result<Cents>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_WRITE);
    if (denied) return Promise.resolve(denied);
    return this.reservations.writeOccupancy(id, spans);
  }

  setBillingMode(id: ReservationId, mode: BillingMode): Promise<Result<number>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_WRITE);
    if (denied) return Promise.resolve(denied);
    return this.reservations.setBillingMode(id, mode);
  }

  moveCharges(chargeIds: readonly ChargeId[], folioId: FolioId): Promise<Result<number>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_WRITE);
    if (denied) return Promise.resolve(denied);
    return this.reservations.moveCharges(chargeIds, folioId);
  }

  markVadDone(id: ReservationId): Promise<Result<void>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_WRITE);
    if (denied) return Promise.resolve(denied);
    return this.reservations.markVadDone(id);
  }

  markTransferReceived(id: ReservationId): Promise<Result<void>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_WRITE);
    if (denied) return Promise.resolve(denied);
    return this.reservations.markTransferReceived(id);
  }
}

/** The cardex — « Clients » and the booking form's picker (PMS-CARDEX-BRIEF
 *  §6.1). `pms.guest.read` / `.write`, the capabilities the RPCs require. */
export class GuestServiceImpl implements GuestService {
  constructor(
    private readonly guestRepo: GuestRepository,
    private readonly actor: ActorSource,
  ) {}

  search(q: GuestSearch): Promise<Result<GuestSearchResult>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_GUEST_READ);
    if (denied) return Promise.resolve(denied);
    return this.guestRepo.search(q);
  }

  sheet(id: GuestId): Promise<Result<GuestSheet>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_GUEST_READ);
    if (denied) return Promise.resolve(denied);
    return this.guestRepo.sheet(id);
  }

  save(sheet: GuestSheet, patch: GuestSheetPatch): Promise<Result<void>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_GUEST_WRITE);
    if (denied) return Promise.resolve(denied);
    const errors = Object.values(pmsValidateGuestSheet(sheet, patch));
    if (errors.length > 0) return Promise.resolve(err('validation', errors.join(' · ')));
    // Nothing changed is not a save — and not a round trip either.
    if (Object.keys(patch).length === 0) return Promise.resolve(ok(undefined));
    return this.guestRepo.saveSheet(sheet.id, patch);
  }

  subtypes(): Promise<Result<CustomerSubtype[]>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_GUEST_READ);
    if (denied) return Promise.resolve(denied);
    return this.guestRepo.listSubtypes();
  }
}

export class ReferenceServiceImpl implements ReferenceService {
  constructor(
    private readonly roomRepo: RoomRepository,
    private readonly rateRepo: RateRepository,
    private readonly actor: ActorSource,
  ) {}

  /**
   * P2 onboarding — bind a room type to its Channex counterpart.
   *
   * VALIDATED HERE, at the boundary, rather than trusted: the most likely wrong
   * value is the room type's NAME, which looks nothing like a UUID and would
   * otherwise be stored without complaint and surface days later as a booking
   * that dead-letters. Normalised for the reason the webhook learned on
   * 2026-08-17 — a UUID is case-insensitive (RFC 4122), and comparing one
   * byte-exactly makes the case a channel happens to send part of our routing.
   */
  setChannexRoomTypeId(id: RoomTypeId, channexId: string | null): Promise<Result<void>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_ROOM_WRITE);
    if (denied) return Promise.resolve(denied);
    const value = pmsNormalizeChannexId(channexId);
    if (!pmsIsChannexId(value)) {
      return Promise.resolve(
        err('validation', `« ${channexId ?? ''} » n'est pas un identifiant Channex (UUID attendu)`),
      );
    }
    return this.roomRepo.setChannexRoomTypeId(id, value);
  }

  channexRatePlans(): Promise<Result<ChannexRatePlanLink[]>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RATE_READ);
    if (denied) return Promise.resolve(denied);
    return this.rateRepo.listChannexRatePlans();
  }

  /** Bind ONE (rate plan × room type) pair — Channex's shape, not ours (0102). */
  setChannexRatePlan(
    ratePlanId: RatePlanId,
    roomTypeId: RoomTypeId,
    channexId: string | null,
  ): Promise<Result<void>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RATE_WRITE);
    if (denied) return Promise.resolve(denied);
    const value = pmsNormalizeChannexId(channexId);
    if (!pmsIsChannexId(value)) {
      return Promise.resolve(
        err('validation', `« ${channexId ?? ''} » n'est pas un identifiant Channex (UUID attendu)`),
      );
    }
    return this.rateRepo.setChannexRatePlan(ratePlanId, roomTypeId, value);
  }

  saveProduct(draft: ProductDraft): Promise<Result<ProductId>> {
    // The SAME capability the table's RLS policy requires, so L2 and L4 refuse
    // the same caller for the same reason (ADR-0013 §4). `pms.rate.write` and
    // not a new one: the catalogue is priced reference data, which is what that
    // capability already means.
    const denied = gate(this.actor, CAPABILITIES.PMS_RATE_WRITE);
    if (denied) return Promise.resolve(denied);
    return this.rateRepo.upsertProduct(draft);
  }

  setProductActive(id: ProductId, active: boolean): Promise<Result<void>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RATE_WRITE);
    if (denied) return Promise.resolve(denied);
    return this.rateRepo.setProductActive(id, active);
  }

  roomTypes(): Promise<Result<RoomType[]>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_ROOM_READ);
    if (denied) return Promise.resolve(denied);
    return this.roomRepo.listRoomTypes();
  }
  rooms(): Promise<Result<Room[]>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_ROOM_READ);
    if (denied) return Promise.resolve(denied);
    return this.roomRepo.listRooms();
  }
  bedConfigs(): Promise<Result<BedConfig[]>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_ROOM_READ);
    if (denied) return Promise.resolve(denied);
    return this.roomRepo.listBedConfigs();
  }

  bedTypes(): Promise<Result<BedType[]>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_ROOM_READ);
    if (denied) return Promise.resolve(denied);
    return this.roomRepo.listBedTypes();
  }

  roomBeds(): Promise<Result<RoomBed[]>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_ROOM_READ);
    if (denied) return Promise.resolve(denied);
    return this.roomRepo.listRoomBeds();
  }
  ratePlans(): Promise<Result<RatePlan[]>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RATE_READ);
    if (denied) return Promise.resolve(denied);
    return this.rateRepo.listPlans();
  }
  products(): Promise<Result<Product[]>> {
    // pms.rate.read, matching the policy on pms_product: a priced catalogue is
    // rate data. One gate per layer, naming the same capability (ADR-0013).
    const denied = gate(this.actor, CAPABILITIES.PMS_RATE_READ);
    if (denied) return Promise.resolve(denied);
    return this.rateRepo.listProducts();
  }
  updateRoomConfig(roomId: RoomId, patch: RoomConfigPatch): Promise<Result<void>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_ROOM_WRITE);
    if (denied) return Promise.resolve(denied);
    return this.roomRepo.updateConfig(roomId, patch);
  }
  createRoom(draft: NewRoom): Promise<Result<RoomId>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_ROOM_WRITE);
    if (denied) return Promise.resolve(denied);
    return this.roomRepo.createRoom(draft);
  }
  deleteRoom(roomId: RoomId): Promise<Result<void>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_ROOM_WRITE);
    if (denied) return Promise.resolve(denied);
    return this.roomRepo.deleteRoom(roomId);
  }
}

/**
 * The rate grid — read the window, store hand edits (R14/R15).
 *
 * Thin on purpose. The grid has no cross-row invariant for a service to own:
 * every rule a rate row has is a CHECK on the row, and `pms_rate` is RLS-
 * audited rather than RPC-gated (D4, the shape `upsertRates` and the room
 * blocks already use). So this layer is the CAPABILITY gate and nothing else —
 * L2 refusing the same caller L4 would, for the same reason.
 */
export class RateGridServiceImpl implements RateGridService {
  constructor(
    private readonly rateRepo: RateRepository,
    private readonly actor: ActorSource,
  ) {}

  grid(from: IsoDate, to: IsoDate): Promise<Result<Rate[]>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RATE_READ);
    if (denied) return Promise.resolve(denied);
    return this.rateRepo.listRateGrid(from, to);
  }

  save(rates: Rate[]): Promise<Result<void>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RATE_WRITE);
    if (denied) return Promise.resolve(denied);
    // An empty save is a no-op, not a round trip: the editor calls this on
    // « Enregistrer » whether or not anything changed, and a PostgREST upsert
    // of zero rows is a request that can only fail.
    if (rates.length === 0) return Promise.resolve(ok(undefined));
    return this.rateRepo.upsertRates(rates);
  }
}

/**
 * The parameter-set service (R14, rate brief §13).
 *
 * Thin on purpose: every rule that could live in the domain already does.
 * `validateRateParameterSet` is pure and lives in `pms-core`; the draft/
 * published freeze is enforced by the DATABASE (0107), not here, so no code
 * path can skip it. What this layer adds is the two things it is for — the L2
 * capability gate, and a refusal that is a French sentence rather than a
 * PostgREST error.
 */
export class RateParameterServiceImpl implements RateParameterService {
  constructor(
    private readonly rateRepo: RateRepository,
    private readonly actor: ActorSource,
  ) {}

  list(): Promise<Result<StoredRateParameterSet[]>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RATE_READ);
    if (denied) return Promise.resolve(denied);
    return this.rateRepo.listParameterSets();
  }

  get(id: number): Promise<Result<StoredRateParameterSet | null>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RATE_READ);
    if (denied) return Promise.resolve(denied);
    return this.rateRepo.getParameterSet(id);
  }

  createDraft(draft: RateParameterSet, note: string | null): Promise<Result<number>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RATE_WRITE);
    if (denied) return Promise.resolve(denied);
    return this.rateRepo.createParameterSetDraft(draft, note);
  }

  updateDraft(id: number, draft: RateParameterSet, note: string | null): Promise<Result<void>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RATE_WRITE);
    if (denied) return Promise.resolve(denied);
    // NOT validated — see the port. An unfinished draft must be savable, or the
    // operator has to author a whole year without ever closing the tab.
    return this.rateRepo.updateParameterSetDraft(id, draft, note);
  }

  async publish(id: number): Promise<Result<void>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RATE_WRITE);
    if (denied) return denied;

    // Validation applies HERE and not on save, because this is the irreversible
    // step: a published set is frozen and can only be archived, so a structural
    // error caught now is caught before it is permanent. Re-read rather than
    // trusting a set the caller passes in — publishing must validate what is
    // actually stored, not what the screen believes it stored.
    const stored = await this.rateRepo.getParameterSet(id);
    if (!stored.ok) return stored;
    if (stored.value === null) {
      return err('not_found', "Ce jeu de paramètres n'existe pas.");
    }

    const problems = validateRateParameterSet(stored.value.parameters);
    if (problems.length > 0) {
      // Every problem, not the first: an operator fixing them one round trip at
      // a time is the reason `validateRateParameterSet` returns a list.
      return err(
        'validation',
        `Ce jeu de paramètres ne peut pas être publié : ${problems.join(' ')}`,
      );
    }
    return this.rateRepo.publishParameterSet(id);
  }

  archive(id: number): Promise<Result<void>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RATE_WRITE);
    if (denied) return Promise.resolve(denied);
    return this.rateRepo.archiveParameterSet(id);
  }

  deleteDraft(id: number): Promise<Result<void>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RATE_WRITE);
    if (denied) return Promise.resolve(denied);
    return this.rateRepo.deleteParameterSetDraft(id);
  }
}

export class AvailabilityServiceImpl implements AvailabilityService {
  constructor(
    private readonly availability: AvailabilityQuery,
    private readonly actor: ActorSource,
  ) {}

  calendar(from: IsoDate, to: IsoDate): Promise<Result<AvailabilityCell[]>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_READ);
    if (denied) return Promise.resolve(denied);
    return this.availability.range(from, to);
  }
  freeRooms(roomTypeId: RoomTypeId, checkin: IsoDate, checkout: IsoDate): Promise<Result<RoomId[]>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_READ);
    if (denied) return Promise.resolve(denied);
    return this.availability.freeRooms(roomTypeId, checkin, checkout);
  }
}

/**
 * « Carte » (till brief §16). The same capabilities the table's policies require (T2): read under `pms.rate.read`,
 * write under `pms.catalogue.write` — held by the operator (wildcard) and the desk (20261019230100). The draft rules
 * are checked before the round trip so the page can answer in French; the database refuses a bad row anyway.
 */
/** « Conditions » (PB7a): reads under pms.rate.read, writes under pms.rate.write — the capabilities the table's
 *  policies require, so L2 and L4 refuse the same caller. A draft is checked by pms-core before any round trip. */
export class CancellationPolicyServiceImpl implements CancellationPolicyService {
  constructor(
    private readonly repo: CancellationPolicyRepository,
    private readonly actor: ActorSource,
  ) {}

  policies(): Promise<Result<StoredCancellationPolicy[]>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RATE_READ);
    if (denied) return Promise.resolve(denied);
    return this.repo.listPolicies();
  }

  savePolicy(draft: Omit<CancellationPolicy, 'verifiedAt'> & { id?: number }): Promise<Result<number>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RATE_WRITE);
    if (denied) return Promise.resolve(denied);
    const problem = cancellationPolicyProblem(draft);
    if (problem !== null) return Promise.resolve(err('validation', problem));
    return this.repo.savePolicy(draft);
  }

  deletePolicy(id: number): Promise<Result<void>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RATE_WRITE);
    if (denied) return Promise.resolve(denied);
    return this.repo.deletePolicy(id);
  }

  verifyPolicy(id: number): Promise<Result<void>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RATE_WRITE);
    if (denied) return Promise.resolve(denied);
    return this.repo.verifyPolicy(id);
  }

  assignPlanPolicy(ratePlanId: number, policyId: number | null): Promise<Result<void>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RATE_WRITE);
    if (denied) return Promise.resolve(denied);
    return this.repo.assignPlanPolicy(ratePlanId, policyId);
  }
}

/** « Virement » (PB10): the property's bank-transfer instructions. Read under pms.reservation.read — the desk renders
 *  them into the confirmation mail — and written under pms.rate.write with the policies: the capabilities the table's
 *  policies require, so L2 and L4 refuse the same caller. */
export class BankTransferServiceImpl implements BankTransferService {
  constructor(
    private readonly repo: BankTransferRepository,
    private readonly actor: ActorSource,
  ) {}

  details(): Promise<Result<BankTransferDetails | null>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_READ);
    if (denied) return Promise.resolve(denied);
    return this.repo.get();
  }

  save(details: Omit<BankTransferDetails, 'propertyCode'>): Promise<Result<void>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RATE_WRITE);
    if (denied) return Promise.resolve(denied);
    const problem = bankTransferProblem(details);
    if (problem !== null) return Promise.resolve(err('validation', problem));
    return this.repo.save(details);
  }
}

export class TillCatalogueServiceImpl implements TillCatalogueService {
  constructor(
    private readonly repo: TillCatalogueRepository,
    private readonly actor: ActorSource,
  ) {}

  carte(): Promise<Result<TillCarte>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RATE_READ);
    if (denied) return Promise.resolve(denied);
    return this.repo.loadCarte();
  }

  async saveTillProduct(draft: TillProductDraft): Promise<Result<ProductId>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_CATALOGUE_WRITE);
    if (denied) return denied;
    const carte = await this.repo.loadCarte();
    if (!carte.ok) return carte;
    const problem = tillDraftProblem(draft, carte.value);
    if (problem !== null) return err('validation', problem);
    return this.repo.upsertTillProduct(draft);
  }

  setTillProductActive(id: ProductId, active: boolean): Promise<Result<void>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_CATALOGUE_WRITE);
    if (denied) return Promise.resolve(denied);
    return this.repo.setTillProductActive(id, active);
  }
}
